import type { Getter, Setter } from 'jotai';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import { ANNOTATION_TYPE } from '../../../../labelling/constants';
import type { AnnotationT } from '../../../../labelling/types';
import { createKeyFrame } from '../../../../labelling/utils';
import { snapshotAnnotationFrames, toFrameWriteEntries } from '../../../utils/annotation-frame-snapshot';
import { diffFrameSpans } from '../../../utils/frame-span-utils';
import { annotationIdToSpanAtom } from '../../side-panel-atoms';
import { studioStoreAtom } from '../../studio-store';
import { type AnnotationFrameWriteEntry, STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import { draftFrameClassificationAtom, selectedAnnotationIdsAtom, showAnnotationDetailsAtom } from '../../tool-atoms';
import type { ApplyFrameClassificationSpanEventPayload } from '../events';
const getFrameRangeIndices = (start: number, end: number): number[] => Array.from({
  length: end - start + 1
}, (_, index) => start + index);
export const applyFrameClassificationSpanCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<ApplyFrameClassificationSpanEventPayload, void> => {
  let previousFramesByIndex: Record<number, AnnotationT> = {};
  let previousAnswers: ApplyFrameClassificationSpanEventPayload['answers'] | undefined;
  let previousDraftFrameClassification = get(draftFrameClassificationAtom);
  let previousSelectedIds: string[] = [];
  let previousShowAnnotationDetails = false;
  const applySpan = async (payload: ApplyFrameClassificationSpanEventPayload) => {
    const {
      annotationId,
      annotationLabel,
      answers,
      newSpan
    } = payload;
    const oldSpan = get(annotationIdToSpanAtom)[annotationId]?.[0];
    const studioStore = get(studioStoreAtom);
    const previousMetadata = studioStore?.getAnnotationMetadata().get(annotationId);
    previousFramesByIndex = {};
    previousAnswers = previousMetadata?.answers;
    previousDraftFrameClassification = get(draftFrameClassificationAtom);
    previousSelectedIds = get(selectedAnnotationIdsAtom);
    previousShowAnnotationDetails = get(showAnnotationDetailsAtom);
    if (studioStore && previousMetadata && oldSpan) {
      previousFramesByIndex = await snapshotAnnotationFrames(studioStore, previousMetadata, [annotationLabel], annotationId, oldSpan.start, oldSpan.end);
    }
    const {
      toDelete,
      toWrite
    } = diffFrameSpans(oldSpan, newSpan);
    if (toDelete.length > 0) {
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_DELETE,
        deletes: toDelete.map(frameIndex => ({
          frameIndex,
          annotationId
        }))
      });
    }
    if (toWrite.length > 0) {
      const writes: AnnotationFrameWriteEntry[] = toWrite.map(frameIndex => ({
        frameIndex,
        annotationId,
        annotation: {
          annotationType: ANNOTATION_TYPE.FRAME_CLASSIFICATION,
          annotationLabel,
          keyFrame: createKeyFrame(false),
          data: null,
          metadata: {
            answers
          }
        }
      }));
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
        writes
      });
    }
    set(dispatchStudioStoreWriteAtom, {
      type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
      annotationId,
      partialPayload: {
        answers
      }
    });
    set(selectedAnnotationIdsAtom, [annotationId]);
    set(draftFrameClassificationAtom, null);
  };
  return {
    execute: applySpan,
    undo: payload => {
      const {
        annotationId,
        newSpan
      } = payload;
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_DELETE,
        deletes: getFrameRangeIndices(newSpan.start, newSpan.end).map(frameIndex => ({
          frameIndex,
          annotationId
        }))
      });
      const writes = toFrameWriteEntries(annotationId, previousFramesByIndex);
      if (writes.length > 0) {
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
          writes
        });
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
        annotationId,
        partialPayload: {
          answers: previousAnswers
        }
      });
      set(selectedAnnotationIdsAtom, previousSelectedIds);
      set(draftFrameClassificationAtom, previousDraftFrameClassification);
      set(showAnnotationDetailsAtom, previousShowAnnotationDetails);
    },
    redo: async payload => {
      await applySpan(payload);
      set(showAnnotationDetailsAtom, false);
    }
  };
};